import type {NavFolderMeta, NavPosterMeta, NavSearchContentAjaxResponse, NavSearchReduxState, NavSearchSectionData, NavSearchSectionRecentsPayload} from '@Components/nav-search/nav-search.types';
import {getEmptyNavSearchSectionData} from '@Components/nav-search/nav-search.types';
import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import type {TemplateSearchGalleryItem} from '@Components/template-search-content/components/template-search-gallery-content/template-search-gallery-content.types';
import {SearchPillTypes} from '@Components/template-search-content/template-search-content.types';

const navSearchInitialState: NavSearchReduxState = {
  isInitialContentLoading: true,
  isMyDesignContentLoading: false,
  isSectionContentLoading: false,
  searchTerm: '',
  selectedContentType: SearchPillTypes.TEMPLATE,
  gallerySearchItems: [],
  trendingItems: [],
  userCreatedPosters: [],
  userCreatedFolders: [],
  userSearchPosters: [],
  sectionRecents: getEmptyNavSearchSectionData(),
  sectionResults: getEmptyNavSearchSectionData(),
  loadedRecentScopes: [],
};

const navSearchSlice = createSlice({
  name: 'navSearchSlice',
  initialState: navSearchInitialState,
  reducers: {
    onNavSearchFirstTimeContentLoad: (state, action: PayloadAction<NavSearchContentAjaxResponse>): void => {
      const content = action.payload;

      state.gallerySearchItems = content.gallerySearchItems;
      state.trendingItems = content.trendingItems;
      state.userCreatedPosters = content.posters;
      state.userCreatedFolders = content.folders;
      state.isInitialContentLoading = false;
    },
    onNavSearchUpdateIsInitialContentLoading: (state, action: PayloadAction<boolean>): void => {
      state.isInitialContentLoading = action.payload;
    },
    onNavSearchUpdateIsMyDesignContentLoading: (state, action: PayloadAction<boolean>): void => {
      state.isMyDesignContentLoading = action.payload;
    },
    onNavSearchUpdateIsSectionContentLoading: (state, action: PayloadAction<boolean>): void => {
      state.isSectionContentLoading = action.payload;
    },
    onNavSearchUpdateSearchTerm: (state, action: PayloadAction<string>): void => {
      state.searchTerm = action.payload;
    },
    onNavSearchUpdateSelectedContentType: (state, action: PayloadAction<SearchPillTypes>): void => {
      state.selectedContentType = action.payload;
    },
    onNavSearchUpdateGallerySearchItems: (state, action: PayloadAction<TemplateSearchGalleryItem[]>): void => {
      state.gallerySearchItems = action.payload;
    },
    onNavSearchUpdateTrendingItems: (state, action: PayloadAction<TemplateSearchGalleryItem[]>): void => {
      state.trendingItems = action.payload;
    },
    onNavSearchUpdateUserCreatedPosters: (state, action: PayloadAction<NavPosterMeta[]>): void => {
      state.userCreatedPosters = action.payload;
    },
    onNavSearchUpdateUserCreatedFolders: (state, action: PayloadAction<NavFolderMeta[]>): void => {
      state.userCreatedFolders = action.payload;
    },
    onNavSearchUpdateUserSearchPosters: (state, action: PayloadAction<NavPosterMeta[]>): void => {
      state.userSearchPosters = action.payload;
    },
    onNavSearchSectionRecentsLoaded: (state, action: PayloadAction<NavSearchSectionRecentsPayload>): void => {
      state.sectionRecents = {...state.sectionRecents, ...action.payload.data};

      if (!state.loadedRecentScopes.includes(action.payload.scope)) {
        state.loadedRecentScopes.push(action.payload.scope);
      }
    },
    onNavSearchUpdateSectionResults: (state, action: PayloadAction<Partial<NavSearchSectionData>>): void => {
      state.sectionResults = {...state.sectionResults, ...action.payload};
    },
  },
});
export const {
  onNavSearchFirstTimeContentLoad,
  onNavSearchUpdateIsInitialContentLoading,
  onNavSearchUpdateIsMyDesignContentLoading,
  onNavSearchUpdateIsSectionContentLoading,
  onNavSearchUpdateSearchTerm,
  onNavSearchUpdateSelectedContentType,
  onNavSearchUpdateGallerySearchItems,
  onNavSearchUpdateTrendingItems,
  onNavSearchUpdateUserCreatedPosters,
  onNavSearchUpdateUserCreatedFolders,
  onNavSearchUpdateUserSearchPosters,
  onNavSearchSectionRecentsLoaded,
  onNavSearchUpdateSectionResults,
} = navSearchSlice.actions;

export const navSearchReducer = navSearchSlice.reducer;
